
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { AIStaffMember } from "@/lib/types";
import { UserMinus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { staffApiClient } from "@/api/staff";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserBusiness } from "@/hooks/staff/useUserBusiness";
import { useQuery } from "@tanstack/react-query";

// Import refactored components
import NoStaffView from "../management/NoStaffView";
import TerminatedStaffView from "../management/TerminatedStaffView";
import StaffProfile from "../management/StaffProfile";
import QuickActions from "../management/QuickActions";
import TerminateDialog from "../management/TerminateDialog";

interface ManagementStageProps {
  aiStaff: AIStaffMember | null;
  staffId?: string;
  isSubmitting: boolean;
  onCancelSubscription: () => void; // Keeping this prop for compatibility
}

const ManagementStage: React.FC<ManagementStageProps> = ({ 
  aiStaff, 
  staffId,
  isSubmitting, // Keeping this prop for compatibility
  onCancelSubscription // Keeping this prop for compatibility
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [isTerminating, setIsTerminating] = useState(false);
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  
  // Get current user and business ID
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 300000, // 5 minutes
  });
  
  const { data: businessId } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
  });

  const handleTerminateStaff = async () => {
    if (!aiStaff || !staffId || !businessId) {
      toast({
        title: "Error",
        description: "Missing required information to terminate staff.",
        variant: "destructive",
      });
      return;
    }
    
    setIsTerminating(true);
    try {
      // Pass the correct businessId to the staffApiClient
      const success = await staffApiClient.terminateStaff(businessId, staffId);
      
      if (!success) throw new Error("Failed to terminate staff member");
      
      toast({
        title: "Staff Terminated",
        description: `${aiStaff.name} has been removed from your team.`,
      });
      
      // Clear any cached staff data
      staffApiClient.clearCache();
      
      navigate("/app/staff");
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to terminate staff member.",
        variant: "destructive",
      });
    } finally {
      setIsTerminating(false);
      setShowTerminateDialog(false);
    }
  };

  // If no AI staff, display the no staff view
  if (!aiStaff) {
    return <NoStaffView />;
  }

  // If AI staff is inactive, display the terminated staff view
  if (aiStaff.hasOwnProperty('is_active') && !aiStaff.is_active) {
    return <TerminatedStaffView aiStaff={aiStaff} />;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
          <h3 className="text-2xl font-bold">AI Staff Management</h3>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 self-start"
            onClick={() => setShowTerminateDialog(true)}
          >
            <UserMinus className="h-4 w-4" />
            Terminate Staff
          </Button>
        </div>
        <p className="text-muted-foreground">
          Manage your AI staff member and track their performance
        </p>
      </div>
      
      <StaffProfile aiStaff={aiStaff} staffId={staffId} />
      
      <QuickActions staffId={staffId} />
      
      <TerminateDialog 
        aiStaff={aiStaff}
        open={showTerminateDialog}
        isTerminating={isTerminating}
        onOpenChange={setShowTerminateDialog}
        onTerminate={handleTerminateStaff}
      />
    </div>
  );
};

export default ManagementStage;
